import { useEffect, useMemo, useState } from "react";
import { useNotify } from "react-admin";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  alpha,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useActivePartner } from "@/hooks/useActivePartner";
import { ResourceName, type DateYMDString } from "@/types";
import { ButtonStyleType, ColorType } from "@/types/shared/uiComponents";
import Button from "@/components/ui/Button";
import { CenteredCircularLoading } from "@/components/common/CenteredCircularLoading";
import { useLaseredConditions } from "@/resources/policies/hooks/useLaseredConditions";
import { resolveDeductibleReview, useDeductibleReviews } from "@/resources/claimants/hooks/useDeductibleReviews";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatDate, todayUtcYMD } from "@/utils/formatters/dateFormatter";
import { reviewByError, reviewByHasPassed } from "@/utils/helpers/expectedTrigger";
import type { DeductibleReviewCandidate, ResolveDeductibleReviewRequest } from "@/types/deductibleReview";
import {
  ASD_NOT_HELD_TEXT,
  candidateLabel,
  laterTriggerInForce,
  laterTriggerText,
  overdueChoiceText,
  reviewHelperText,
} from "./reviewHelperText";

interface DeductibleReviewDialogProps {
  open: boolean;
  onClose: () => void;
  claimantId: string;
  policyId: string;
  claimantName?: string;
  /**
   * Called after a successful resolve, before `onClose`. The opener owns the
   * deferred recompute refresh (`useRecomputeRefreshOrStandalone`): this dialog
   * unmounts on close, which would cancel any refetch timers it started, so off
   * the claimant page the flag would never clear.
   */
  onResolved?: () => void;
  /**
   * The policy uses ASD allocation, which the hold does not stop. Passed where
   * the policy is already loaded (claimant page); elsewhere omitted.
   */
  asdPolicy?: boolean;
}

/**
 * The dialog renders in a portal, but React bubbles its events through the
 * component tree: opened from a flag inside a clickable row (claimant list,
 * Lasered Claimants tab), a click, mouse-down or key press in the dialog would
 * reach the row and navigate away. Stop them at the dialog root; MUI's own
 * backdrop and Escape handling run before this.
 */
const stopBubbling = (e: { stopPropagation: () => void }) => e.stopPropagation();

const money = (n?: number) =>
  n === undefined ? "—" : formatUSD(n, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

interface StoplossRow {
  total_paid_amount?: number | string;
  claim_status?: string;
  aura_submitted_at?: string;
  carrier_submitted_at?: string;
  policy_id_ref?: string;
}

/** Submitted (frozen) SLC total for the increase-after-submission notice. */
const useSubmittedSlcTotal = (claimantId: string, policyId: string, enabled: boolean) =>
  useQuery({
    queryKey: ["deductible-review-submitted-slcs", claimantId, policyId],
    queryFn: async (): Promise<number> => {
      // This policy's claims only, as the server's frozen_slc counts them (!4880
      // thread 30534): unfiltered, the list is 100 rows across every policy.
      const { json } = await httpClient(
        `${getPartnerAwareApiUrl()}/claimants/${claimantId}/stoploss-claims?policy_id_ref=${encodeURIComponent(policyId)}`
      );
      const rows = ((json as { data?: StoplossRow[] })?.data ?? (Array.isArray(json) ? json : [])) as StoplossRow[];
      return rows
        .filter((s) => s.policy_id_ref === policyId)
        .filter((s) => s.carrier_submitted_at || s.aura_submitted_at || (s.claim_status && s.claim_status !== "new"))
        .reduce((sum, s) => sum + (Number(s.total_paid_amount) || 0), 0);
    },
    enabled,
  });

/**
 * Pick the deductible that applies when the resolution is ambiguous (#7346).
 * Precedent: DuplicateClaimantPairReviewDialog (side by side, differences
 * highlighted, required reason, decision recorded server-side). Everyone sees
 * the review; resolving needs claimant write access (claimants create or edit),
 * which the API enforces with a 403. A read-only user sees the review with the
 * inputs off and no Confirm, and is told why.
 */
/** ADR-035 read-only partner write guard: a 400 with code PARTNER_READ_ONLY (as useReviewApproval). */
const isPartnerReadOnlyError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const { status, body } = error as { status?: number; body?: unknown };
  return (
    status === 400 && !!body && typeof body === "object" && (body as { code?: unknown }).code === "PARTNER_READ_ONLY"
  );
};

const PARTNER_READ_ONLY_TEXT =
  "Decisions can't be saved under this partner (read-only). Switch to the claimant's partner and try again.";

export const DeductibleReviewDialog = ({
  open,
  onClose,
  claimantId,
  policyId,
  claimantName,
  onResolved,
  asdPolicy,
}: DeductibleReviewDialogProps) => {
  const notify = useNotify();
  const { canEdit, canCreate, isLoading: permissionsLoading } = useResourcePermissions(ResourceName.CLAIMANTS);
  // A read-only partner (ADR-035) can't save a decision either: say so before
  // the form is filled in, not after the server rejects it (!4880 thread 30534).
  const { readOnly: partnerReadOnly } = useActivePartner(open);
  const canResolve = (canEdit || canCreate) && !partnerReadOnly;
  const readOnly = !permissionsLoading && !canResolve;

  const { openReview, isLoading, refetch, error: loadError } = useDeductibleReviews(claimantId, policyId, open);
  const { conditions } = useLaseredConditions(policyId, claimantId, open);
  const isIncrease = openReview?.reason === "increase_after_submission";
  const { data: submittedTotal } = useSubmittedSlcTotal(claimantId, policyId, open && isIncrease);

  const [choice, setChoice] = useState<string>("");
  const [reason, setReason] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staleMessage, setStaleMessage] = useState("");
  // The decision is saved but the server could not finish the follow-up (the
  // recompute was not queued, or a merged laser was not rewritten). Repeating
  // the same decision is idempotent and retries it.
  const [retry, setRetry] = useState<{
    reviewId: string;
    body: ResolveDeductibleReviewRequest;
    warning: string;
  } | null>(null);
  // expected_trigger_overdue (#7361): keeping the expected trigger needs a new review-by date.
  const [reviewBy, setReviewBy] = useState("");

  // Reset when the dialog opens or a different review loads (e.g. after supersede).
  useEffect(() => {
    if (!open) return;
    setChoice("");
    setReason("");
    setAcknowledged(false);
    setReviewBy("");
  }, [open, openReview?.id]);

  useEffect(() => {
    if (!open) {
      setStaleMessage("");
      setRetry(null);
    }
  }, [open]);

  const byId = useMemo(() => new Map(conditions.map((c) => [c.id, c])), [conditions]);
  const candidates = openReview?.candidates ?? [];
  const amountDiffers = new Set(candidates.map((c) => c.amt_deductible)).size > 1;
  const dateDiffers = new Set(candidates.map((c) => c.effective_date)).size > 1;
  const differs = [amountDiffers && "deductible", dateDiffers && "start date"].filter(Boolean).join(" and ");

  // The carrier acknowledgment applies to every choice in a review opened behind
  // a submitted stop-loss claim (#7346 AC3, PO 2026-10-08: whatever is chosen,
  // the submission stays as filed). The API does not take it; it is a
  // confirmation here.
  const chosen = candidates.find((c) => c.id === choice);
  const needsCarrierAck = isIncrease && !!chosen;

  // Overdue expected trigger: a conditional laser keeps it (new review-by date);
  // the straight laser or policy base deductible cancels it (as the API resolves).
  const isOverdue = openReview?.reason === "expected_trigger_overdue";
  const laterTrigger = openReview ? laterTriggerInForce(openReview, conditions) : undefined;
  const keepsExpected = isOverdue && chosen?.source === "conditional_laser";
  const expectedCondition = keepsExpected && chosen?.source_ref_id ? byId.get(chosen.source_ref_id) : undefined;
  const reviewByProblem = keepsExpected
    ? reviewByError(reviewBy, expectedCondition?.expected_trigger?.expected_date, expectedCondition) ||
      (reviewByHasPassed(reviewBy) ? "Must be today or later." : "")
    : "";

  const canConfirm =
    canResolve &&
    !!openReview &&
    !!chosen &&
    reason.trim().length > 0 &&
    (!needsCarrierAck || acknowledged) &&
    !reviewByProblem &&
    !saving;

  const handleConfirm = async () => {
    if (!openReview || !canConfirm) return;
    setSaving(true);
    const body: ResolveDeductibleReviewRequest = {
      candidate_id: choice,
      decision_reason: reason.trim(),
      ...(keepsExpected && { review_by: reviewBy as DateYMDString }),
    };
    try {
      const saved = await resolveDeductibleReview(claimantId, openReview.id, body);
      if (saved?.warning) {
        // Keep the dialog (and the hold) up so the follow-up can be retried here.
        setRetry({ reviewId: openReview.id, body, warning: saved.warning });
        return;
      }
      notify("Deductible decision recorded. Held stop-loss claims will refile shortly.", { type: "success" });
      onResolved?.();
      onClose();
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (isPartnerReadOnlyError(err)) {
        notify(PARTNER_READ_ONLY_TEXT, { type: "error" });
      } else if (status === 409) {
        // Superseded while open (or the candidate is no longer this review's):
        // never save a stale decision; reload what is current.
        setStaleMessage(
          "This review changed while it was open: new claim lines or terms produced a different set of choices. The current review has been loaded; choose again."
        );
        await refetch();
      } else {
        notify((err as Error)?.message || "Could not record the decision.", { type: "error" });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRetry = async () => {
    if (!retry) return;
    setSaving(true);
    try {
      const saved = await resolveDeductibleReview(claimantId, retry.reviewId, retry.body);
      if (saved?.warning) {
        setRetry({ ...retry, warning: saved.warning });
        return;
      }
      setRetry(null);
      notify("Deductible decision recorded. Held stop-loss claims will refile shortly.", { type: "success" });
      onResolved?.();
      onClose();
    } catch (err) {
      notify((err as Error)?.message || "Could not retry.", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  // MUI radio cards, as the laser-type cards in LaseredClaimantDialog: real
  // radio inputs in one named group, so Tab reaches the group and the arrow keys
  // move the choice natively. The claim-line link sits outside the label, so no
  // control is nested in another (UX review #9).
  const renderCandidate = (c: DeductibleReviewCandidate) => {
    const selected = choice === c.id;
    return (
      <Box
        key={c.id}
        sx={(theme) => ({
          border: selected ? 2 : 1,
          borderRadius: 1.5,
          borderColor: selected ? "primary.main" : "divider",
          bgcolor: selected ? alpha(theme.palette.primary.main, 0.04) : "transparent",
          px: 1.5,
          py: selected ? 1.375 : 1.5,
        })}
      >
        <FormControlLabel
          value={c.id}
          disabled={readOnly}
          data-testid={`review-candidate-${c.id}`}
          control={<Radio size="small" sx={{ p: 0, mt: 0.25, mr: 1.5 }} />}
          sx={{ alignItems: "flex-start", m: 0, width: "100%" }}
          // Phrasing content only inside the <label>.
          disableTypography
          label={
            <Box component="span" sx={{ display: "block" }}>
              <Typography variant="body2" component="span" fontWeight={600} sx={{ display: "block" }}>
                {candidateLabel(c, byId)}
              </Typography>
              <Stack component="span" direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                <Typography variant="body2" component="span" fontWeight={amountDiffers ? 700 : 400}>
                  {money(c.amt_deductible)}
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  fontWeight={dateDiffers ? 700 : 400}
                  color="text.secondary"
                >
                  from {c.effective_date ? formatDate(c.effective_date.slice(0, 10)) : "—"}
                </Typography>
                {c.amt_projected_spend_over_deductible !== undefined && (
                  <Typography variant="body2" component="span" color="text.secondary">
                    Spend over deductible: {money(c.amt_projected_spend_over_deductible)}
                  </Typography>
                )}
              </Stack>
              {isOverdue && (
                <Typography
                  variant="caption"
                  component="span"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                  data-testid={`review-candidate-${c.id}-effect`}
                >
                  {overdueChoiceText(c, candidates, byId)}
                </Typography>
              )}
            </Box>
          }
        />
        {c.triggering_claim_item_id && (
          // New tab: following it must not drop the choice and reason typed here (UX review #7).
          <Link
            variant="body2"
            href={`/#/claim-items/${c.triggering_claim_item_id}/show`}
            target="_blank"
            rel="noopener"
            sx={{ display: "inline-block", ml: 3.5, mt: 0.5 }}
          >
            Triggering claim line
          </Link>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!saving) onClose();
      }}
      fullWidth
      maxWidth="sm"
      aria-labelledby="deductible-review-title"
      onClick={stopBubbling}
      onMouseDown={stopBubbling}
      onKeyDown={stopBubbling}
    >
      <DialogTitle id="deductible-review-title">
        Resolve deductible review{claimantName ? `: ${claimantName}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        {retry ? (
          <Alert severity="warning" role="alert" data-testid="review-retry">
            Your decision is saved. {retry.warning.replace(/\s*Resolve again with the same candidate to retry\.?$/, "")}{" "}
            Retry sends it again.
          </Alert>
        ) : isLoading ? (
          <Box sx={{ py: 4 }}>
            <CenteredCircularLoading size={28} />
          </Box>
        ) : loadError && !openReview ? (
          // Not "no open review": the claims may well still be held (UX review #4).
          <Alert
            severity="error"
            role="alert"
            data-testid="review-load-error"
            action={
              <Button
                variant={ButtonStyleType.Text}
                color={ColorType.Inherit}
                size="small"
                onClick={() => void refetch()}
                data-testid="review-load-retry"
              >
                Retry
              </Button>
            }
          >
            Could not load the deductible review.
          </Alert>
        ) : !openReview ? (
          <Alert severity="success">
            There is no open deductible review for this claimant on this policy. It may have been resolved already.
          </Alert>
        ) : (
          <Stack spacing={2}>
            {readOnly && (
              <Alert severity="info" data-testid="review-no-access">
                {partnerReadOnly ? PARTNER_READ_ONLY_TEXT : "You need claimant edit access to resolve this review."}
              </Alert>
            )}
            {staleMessage && (
              <Alert severity="warning" role="alert">
                {staleMessage}
              </Alert>
            )}
            <Typography variant="body2">
              {reviewHelperText(openReview, conditions).replace(/ Click to review\.$/, "")}
            </Typography>
            {asdPolicy && (
              <Alert severity="info" variant="outlined" data-testid="review-asd-not-held">
                {ASD_NOT_HELD_TEXT}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              In force while held: <strong>{money(openReview.amt_held_deductible)}</strong>
            </Typography>
            {laterTrigger && (
              <Alert severity="info" variant="outlined" data-testid="review-later-trigger">
                {laterTriggerText(laterTrigger)}
              </Alert>
            )}

            {differs && (
              <Typography variant="caption" color="text.secondary" data-testid="review-differs">
                The choices differ in {differs} (shown in bold).
              </Typography>
            )}
            <RadioGroup
              aria-label="Deductible choices"
              name={`deductible-review-${openReview.id}`}
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
            >
              <Stack spacing={1}>{candidates.map(renderCandidate)}</Stack>
            </RadioGroup>

            {needsCarrierAck && (
              <Alert severity="warning" variant="outlined">
                <Typography variant="body2" sx={{ mb: 1 }}>
                  A stop-loss claim of {money(submittedTotal)} was already submitted at the current deductible. This
                  decision does not change that submission; any correction is a conversation with the carrier, outside
                  Coronis.
                </Typography>
                <FormControlLabel
                  disabled={readOnly}
                  control={
                    <Checkbox size="small" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
                  }
                  label="I understand the submitted claim is handled with the carrier"
                />
              </Alert>
            )}

            {keepsExpected && (
              <TextField
                label="New Review-By Date"
                type="date"
                required
                fullWidth
                size="small"
                disabled={readOnly}
                value={reviewBy}
                onChange={(e) => setReviewBy(e.target.value)}
                error={!!reviewBy && !!reviewByProblem}
                helperText={
                  (reviewBy && reviewByProblem) ||
                  `If no qualifying claim line arrives by then, a new review opens.${
                    expectedCondition?.paid_end_date
                      ? ` No later than the paid window's end (${formatDate(expectedCondition.paid_end_date.slice(0, 10))}).`
                      : ""
                  }`
                }
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: {
                    "data-testid": "review-review-by",
                    min: todayUtcYMD(),
                    max: expectedCondition?.paid_end_date?.slice(0, 10),
                  },
                }}
              />
            )}

            <TextField
              label="Reason for this choice"
              required
              fullWidth
              multiline
              minRows={2}
              size="small"
              disabled={readOnly}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              helperText="Recorded with your name and the time, and shown in the Deductible review row on the claimant's Deductible tab."
              slotProps={{ htmlInput: { "data-testid": "review-reason" } }}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {/* Off while saving, so the dialog cannot close under a decision in flight (UX review #17). */}
        <Button variant={ButtonStyleType.Text} onClick={onClose} disabled={saving}>
          {retry || readOnly || (!isLoading && !openReview) ? "Close" : "Cancel"}
        </Button>
        {retry ? (
          <Button
            variant={ButtonStyleType.Contained}
            onClick={handleRetry}
            disabled={saving}
            loading={saving}
            data-testid="review-retry-button"
          >
            Retry
          </Button>
        ) : readOnly ? null : (
          <Button
            variant={ButtonStyleType.Contained}
            onClick={handleConfirm}
            disabled={!canConfirm}
            loading={saving}
            data-testid="review-confirm"
          >
            Confirm deductible
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
